// src/services/proposalService.js
//
// Frontend client for the Wave 1-3 /v1/proposals API.
// Uses the shared apiClient, which attaches the Supabase bearer token and
// recovers from a stale/revoked session (refresh-once-then-retry, else clean
// sign-out) so a dead token no longer surfaces as an un-recoverable 401.
//
// All methods return the canonical `{ data, error }` shape used elsewhere in
// the services layer.
import { apiFetch } from './apiClient';

export const proposalService = {
  /**
   * Respond to a proposal (accept / reject / counter).
   *   action: 'accept' | 'reject' | 'counter'
   */
  async respond({ id, action, selectedSlotId, counterSlots, message } = {}) {
    try {
      const body = { action };
      if (selectedSlotId) body.selected_slot_id = selectedSlotId;
      if (counterSlots) body.counter_slots = counterSlots;
      if (message != null) body.message = message;
      const data = await apiFetch(`/v1/proposals/${id}/response`, {
        method: 'POST',
        body,
      });
      return { data, error: null };
    } catch (error) {
      console.error('Error responding to proposal:', error);
      return { data: null, error };
    }
  },

  async get(id) {
    try {
      const data = await apiFetch(`/v1/proposals/${id}`);
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching proposal:', error);
      return { data: null, error };
    }
  },

  async list({ status, threadId } = {}) {
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (threadId) params.set('thread_id', threadId);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await apiFetch(`/v1/proposals${qs}`);
      return { data, error: null };
    } catch (error) {
      console.error('Error listing proposals:', error);
      return { data: null, error };
    }
  },

  async getThread(id) {
    try {
      const data = await apiFetch(`/v1/proposals/${id}/thread`);
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching proposal thread:', error);
      return { data: null, error };
    }
  },

  async cancel(id) {
    try {
      const data = await apiFetch(`/v1/proposals/${id}/cancel`, {
        method: 'POST',
        body: {},
      });
      return { data, error: null };
    } catch (error) {
      console.error('Error cancelling proposal:', error);
      return { data: null, error };
    }
  },

  async create({ participantIds, slots, message, expiresInHours } = {}) {
    try {
      const body = {
        participant_ids: participantIds,
        slots,
      };
      if (message != null) body.message = message;
      if (expiresInHours != null) body.expires_in_hours = expiresInHours;
      const data = await apiFetch('/v1/proposals', {
        method: 'POST',
        body,
      });
      return { data, error: null };
    } catch (error) {
      console.error('Error creating proposal:', error);
      return { data: null, error };
    }
  },
};
